import { useStudioCtx } from "@/wab/client/studio-ctx/StudioCtx";
import { ViewCtx } from "@/wab/client/studio-ctx/view-ctx";
import { useForceUpdate } from "@/wab/client/useForceUpdate";
import { makeVariantedStylesHelperFromCurrentCtx } from "@/wab/client/utils/style-utils";
import { useSignalListener } from "@/wab/commons/components/use-signal-listener";
import { ANIMATIONS_LOWER } from "@/wab/shared/Labels";
import { VariantCombo } from "@/wab/shared/Variants";
import { spawn } from "@/wab/shared/common";
import { allAnimationSequences } from "@/wab/shared/core/sites";
import { Animation, TplTag } from "@/wab/shared/model/classes";
import { notification } from "antd";
import { ok } from "neverthrow";
import { useState } from "react";

interface UseAnimationsOptions {
  tpl: TplTag;
  variants: VariantCombo;
  viewCtx: ViewCtx;
  isDisabled?: boolean;
}

export function useAnimations(options: UseAnimationsOptions) {
  const { tpl, variants, viewCtx, isDisabled } = options;
  const studioCtx = useStudioCtx();
  const vtm = viewCtx.variantTplMgr();

  // Get animations using VariantTplMgr (handles private vs component variant logic)
  const { animations, definedIndicator } = vtm.getAnimationInfoForVariantCombo(
    tpl,
    variants
  );

  const allAnimSequences = allAnimationSequences(studioCtx.site, {
    includeDeps: "direct",
  });

  const [inspectedIndex, setInspectedIndex] = useState<number | undefined>();
  const inspectedAnimation =
    inspectedIndex !== undefined ? animations?.[inspectedIndex] : undefined;

  const vsh = makeVariantedStylesHelperFromCurrentCtx(studioCtx);

  const focusedTpl = studioCtx.focusedViewCtx()?.focusedTpl();
  const forceUpdate = useForceUpdate();
  useSignalListener(studioCtx.animationChanged, forceUpdate, [studioCtx]);

  const isAnimationPlaying =
    focusedTpl && studioCtx.styleMgrBcast.hasActiveAnimationPreview(focusedTpl);

  const playAnimations = (previewAnimations: Animation[]) => {
    if (previewAnimations.length === 0 || isDisabled || !focusedTpl) {
      return;
    }
    spawn(
      studioCtx.styleMgrBcast.playAnimationPreview(
        focusedTpl,
        previewAnimations
      )
    );
  };

  const stopAnimations = () => {
    if (isAnimationPlaying && focusedTpl) {
      studioCtx.styleMgrBcast.stopAnimationPreview(focusedTpl);
    }
  };

  const triggerAnimationPreviewOnUpdate = (previewAnimations: Animation[]) => {
    stopAnimations();
    playAnimations(previewAnimations);
  };

  const closeInspectedAnimation = () => {
    setInspectedIndex(undefined);
    stopAnimations();
  };

  const onInspectedAnimationChange = () => {
    triggerAnimationPreviewOnUpdate(animations);
  };

  const addAnimationLayer = () => {
    if (allAnimSequences.length === 0) {
      studioCtx.switchLeftTab("animationSequences", { highlight: true });
      notification.warning({
        message: `Please add ${ANIMATIONS_LOWER} in your project.`,
      });
      return;
    }

    spawn(
      studioCtx.change(() => {
        const defaultSequence = allAnimSequences[0];
        const newAnimation = studioCtx.tplMgr().addAnimation(defaultSequence);
        const newAnimations = vtm.addAnimation(tpl, newAnimation, variants);

        setInspectedIndex(newAnimations.length - 1);
        triggerAnimationPreviewOnUpdate(newAnimations);

        return ok();
      })
    );
  };

  const removeAnimation = (animation: Animation) => {
    if (isDisabled) {
      return;
    }

    spawn(
      studioCtx.change(() => {
        const tplAnimations = vtm.removeAnimation(tpl, animation, variants);
        triggerAnimationPreviewOnUpdate(tplAnimations);
        return ok();
      })
    );
  };

  const reorderAnimations = (from: number, to: number) => {
    if (isDisabled) {
      return;
    }

    spawn(
      studioCtx.change(() => {
        const newAnimations = vtm.reorderAnimations(tpl, from, to, variants);
        triggerAnimationPreviewOnUpdate(newAnimations);
        return ok();
      })
    );
  };

  const openAnimationForEditing = (index: number) => {
    if (isDisabled) {
      return;
    }

    spawn(
      studioCtx.change(() => {
        vtm.ensureAnimationsForEditing(tpl, variants);
        setInspectedIndex(index);
        return ok();
      })
    );
  };

  return {
    inspectedAnimation,
    animations,
    vsh,
    isAnimationPlaying: !!isAnimationPlaying,
    addAnimationLayer,
    removeAnimation,
    reorderAnimations,
    openAnimationForEditing,
    closeInspectedAnimation,
    playAnimations,
    stopAnimations,
    onInspectedAnimationChange,
    definedIndicator,
  };
}
